import { Element } from "@js/extends";

export default class FilterCollapse extends Element {
	mounted() {
		this.toggle = this.el.querySelector(".js-filter-collapse-toggle");
		this.content = this.el.querySelector(".js-filter-collapse-content");
		this.items = this.el.querySelector(".js-filter-collapse-items");
		this.storageKey = this.getStorageKey();

		this.isOpen = this.toggle.ariaExpanded === "true";
		this.restoreState();

		this.observer = new IntersectionObserver((entries) => {
			for (const entry of entries) {
				if (entry.isIntersecting && this.isOpen) {
					requestAnimationFrame(() => {
						const height = this.getHeight();

						this.content.style.display = "block";
						this.content.style.height = `${height}px`;
						this.content.style.overflow = "visible";
					});
				}
			}
		});

		this.observer.observe(this.el);
	}

	events() {
		this.toggle.addEventListener("click", (e) => this.handleClick(e));
		this.content.addEventListener("transitionend", () => this.closeTransitionEnd());
	}

	handleClick(e) {
		e?.preventDefault();
		this.isOpen ? this.close() : this.open();
	}

	open() {
		this.updateState(true);
		this.content.style.display = "block";

		requestAnimationFrame(() => {
			this.content.style.height = `${this.getHeight()}px`;
		});

		this.content.addEventListener("transitionend", this.openTransitionEnd, { once: true });
	}

	close() {
		this.updateState(false);
		this.content.style.overflow = "hidden";
		this.content.style.height = "0";
	}

	updateState(isOpen) {
		this.isOpen = isOpen;
		this.toggle.ariaExpanded = isOpen;
		this.content.ariaHidden = !isOpen;
		this.storeState();
	}

	getHeight() {
		return this.items.scrollHeight;
	}

	openTransitionEnd = () => {
		if (this.isOpen) {
			this.content.style.overflow = "visible";
		}
	};

	closeTransitionEnd() {
		if (!this.isOpen) {
			this.content.style.display = "none";
		}
	}

	getStorageKey() {
		const collapseKey = this.el.dataset.filterCollapseKey;

		if (!collapseKey) {
			return null;
		}

		return `filter-collapse:${window.location.pathname}:${collapseKey}`;
	}

	restoreState() {
		if (!this.storageKey) {
			return;
		}

		let value = null;
		try {
			value = window.sessionStorage.getItem(this.storageKey);
		} catch (error) {
			return;
		}

		if (value !== "open" && value !== "closed") {
			return;
		}

		const isOpen = value === "open";
		this.isOpen = isOpen;
		this.toggle.ariaExpanded = isOpen;
		this.content.ariaHidden = !isOpen;

		if (isOpen) {
			this.content.style.display = "block";
			this.content.style.height = "auto";
			this.content.style.overflow = "visible";
			return;
		}

		this.content.style.display = "none";
		this.content.style.height = "0";
		this.content.style.overflow = "hidden";
	}

	storeState() {
		if (!this.storageKey) {
			return;
		}

		try {
			window.sessionStorage.setItem(this.storageKey, this.isOpen ? "open" : "closed");
		} catch (error) {
			// Ignore storage write failures so collapse behavior keeps working.
		}
	}
}
